/**
 * src/lib/api.js
 *
 * Callable-function wrapper + the 503/hang mitigation for the me-central2
 * Firestore Listen-channel condition (see CLAUDE.md "me-central2 Firestore
 * Listen-channel 503 storm" — streaming reads can hang forever while one-shot
 * reads and callables succeed). Every async read/call in this app MUST go
 * through guardedRead so the UI NEVER shows an infinite spinner — it either
 * shows the real value, or a small "couldn't load (offline) — retry" state.
 *
 * Modeled on portal/src/function-api.js's call() wrapper, ported to the
 * Firebase v10 modular SDK (httpsCallable(functions, name) instead of the
 * compat functions().httpsCallable(name) form).
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase.js';

/**
 * Invoke a Cloud Function callable and return its `.data` payload.
 * Throws on failure — callers should route through guardedRead() to get a
 * graceful timeout/error state instead of an unhandled rejection.
 *
 * @param {string} name    — callable export name (e.g. 'getSessionBootstrap')
 * @param {Object} [payload] — request body
 * @returns {Promise<any>}
 */
export async function callFn(name, payload = {}) {
  const fn = httpsCallable(functions, name);
  const result = await fn(payload);
  return result.data;
}

/**
 * Race a one-shot read/call against a timeout so the UI never hangs.
 *
 * NOTE: this does not (and cannot) cancel the underlying network request —
 * it only bounds how long the UI waits before offering a retry. That is the
 * correct mitigation for the Listen-channel 503 storm: the request may still
 * complete later, but the user is never left staring at a spinner.
 *
 * @template T
 * @param {Promise<T>} promise
 * @param {Object} [opts]
 * @param {number} [opts.timeoutMs=9000]
 * @param {string} [opts.label='read']  — for console diagnostics only
 * @returns {Promise<{data: T|null, timedOut: boolean, error: any, denied: boolean}>}
 */
export async function guardedRead(promise, opts = {}) {
  const { timeoutMs = 9000, label = 'read' } = opts;
  let timedOut = false;

  const timeout = new Promise((resolve) => {
    setTimeout(() => {
      timedOut = true;
      resolve(undefined);
    }, timeoutMs);
  });

  try {
    const result = await Promise.race([promise, timeout]);
    if (timedOut) {
      console.warn(`[guardedRead] "${label}" timed out after ${timeoutMs}ms — showing retry state, not an infinite spinner.`);
      return { data: null, timedOut: true, error: null, denied: false };
    }
    return { data: result, timedOut: false, error: null, denied: false };
  } catch (err) {
    // Classify FIRST (before logging): permission-denied / unauthenticated /
    // failed-precondition are an EXPECTED permission boundary — a role-gated read the
    // caller handles via the `denied` flag (e.g. a non-admin viewing a screen that
    // embeds an admin-only widget like System Health). Logging those as console.error is
    // noise, not a failure (and the screen-fuzzer oracle mis-reads it as a broken
    // screen). They are also NOT retryable (same caller identity → same result), so the
    // UI shows a boundary message, not a "retry" link. failed-precondition is the code
    // the portal read-CFs' resolveCaller() throws for a missing/stale tenant claim — an
    // identity failure, classified the same as denied so the UI never falls into an
    // infinite-retry loop. deadline-exceeded/unavailable (the 503/hang class this module
    // mitigates) are deliberately NOT denied — those ARE retryable and still log error.
    const code = String((err && err.code) || '');
    const denied = code.includes('permission-denied') || code.includes('unauthenticated') || code.includes('failed-precondition');
    if (denied) {
      console.debug(`[guardedRead] "${label}" denied — expected permission boundary, handled by caller.`);
    } else {
      console.error(`[guardedRead] "${label}" failed`, err);
    }
    return { data: null, timedOut: false, error: err, denied };
  }
}

/**
 * Classifies a guardedRead() result into the UI-facing failure category so
 * screens can show the right copy: a permission-denied caller should NEVER
 * see a "retry" link (retrying won't change the outcome), while a timeout or
 * transport error should.
 *
 * @param {{timedOut?: boolean, error?: any, denied?: boolean}} result
 * @returns {'denied'|'timeout'|'error'|'ok'}
 */
export function classifyReadFailure(result) {
  if (!result) return 'error';
  if (result.denied) return 'denied';
  if (result.timedOut) return 'timeout';
  if (result.error) return 'error';
  return 'ok';
}
